// PaletteGraphMatches — "IN THIS GRAPH", at the top of the add-node palette.
//
// Round-2 UI notes, two of them answered by one section:
//
//   "Search creates new nodes rather than finding for some node types -
//    Play audio, for instance"
//   "find nodes should search by name / variable value to make it easier to find"
//
// The palette searched a STATIC catalog of node TYPES, so every query was
// answered with "here is a new one". Three `unique` types (Voxy Config,
// Convorcher Session, Head Tracked Camera) were the exception, because a second
// copy is illegal for them — which is why the affordance existed for exactly the
// nodes you are least likely to be hunting for.
//
// Adding one is still legitimate, so the catalog stays and keeps the primary
// slot. What was missing was the CHOICE, and any way at all to search a node by
// what is written ON it — a Make Literal String holding "_face", a Set Variable
// driving HeadFollowWeight — rather than by its type's name.
//
// The matching is `findGraphNodes`, the same walker Ctrl+F uses, so the palette
// and Ctrl+F can never disagree about what is in the graph or where it lives.
// Presentational: the caller passes the graphs snapshot and handles the pick.

import { findGraphNodes, describeLocation, disambiguateLabels } from '../../lib/graphNodeSearch'

const MAX_MATCHES = 8

export default function PaletteGraphMatches({ query, graphs, graph, onPick, limit = MAX_MATCHES }) {
  // Re-derived per keystroke on purpose: `graphs` snapshots module singletons
  // the editor mutates in place, so there is nothing to memo against that would
  // not go stale. A few hundred nodes is nothing next to a keypress.
  const matches = query?.trim()
    // Scoped to the graph the palette is open on: this section is "what is
    // already HERE". Ctrl+F is the cross-graph search, and it stays that way.
    // Disambiguated AFTER the slice, so the qualifier appears only on rows that
    // are actually side by side in front of the user.
    ? disambiguateLabels(
        findGraphNodes(graphs, query, { preferGraph: graph, limit: limit * 4 })
          .filter((m) => m.graph === graph)
          .slice(0, limit),
      )
    : []

  // Nothing to say — render nothing, so a palette used purely to add a node
  // looks exactly as it did before.
  if (!matches.length) return null

  return (
    <div data-testid="palette-matches">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 10px',
        color: 'var(--m-fg-3)', fontSize: 10, letterSpacing: 0.8, userSelect: 'none',
      }}>
        IN THIS GRAPH
      </div>

      {matches.map((m) => {
        // Where it is, or failing that what it is. Suppressed when it would
        // merely repeat the label — a node with no naming data labels itself by
        // its type, and the row then read "playAudio  playAudio".
        const aside = m.container ? m.container.name : m.type
        return (
        <div
          key={m.nodeId}
          data-testid={`palette-match-${m.nodeId}`}
          onClick={() => onPick?.(m)}
          title={`${m.label} — ${describeLocation(m)}`}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
            padding: '6px 10px 6px 22px', cursor: 'pointer',
            borderLeft: '3px solid var(--m-fg-3)',
            marginLeft: 8, marginRight: 6,
            borderRadius: '0 3px 3px 0',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--m-panel-hi)' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
        >
          <span style={{
            color: 'var(--m-fg-2)', fontSize: 11,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{m.label}</span>
          {aside !== m.label && (
            <span style={{
              color: 'var(--m-fg-3)', fontSize: 9, flexShrink: 0,
              maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{aside}</span>
          )}
        </div>
        )
      })}
    </div>
  )
}
